import { Calculator, Loader2, Plus, Scale, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Field, FormSection } from '@/components/common/FormBits'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { confirmModal, Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { Skeleton } from '@/components/ui/skeleton'
import { fmtDate, fmtMoney } from '@/features/shared/format'
import { apiErrorMessage } from '@/i18n'
import { cn } from '@/lib/utils'
import {
  useDeletePayrollParams,
  usePayrollParams,
  usePayrollPreview,
  useSavePayrollParams,
  type PayrollParams,
} from '../../api'

type Props = { open: boolean; canManage: boolean; onClose: () => void }

/** Dönemsel bordro parametreleri ve brütten nete hesaplayıcı. Hesap sunucudaki motorla yapılır. */
export function PayrollParamsModal({ open, canManage, onClose }: Props) {
  const { t } = useTranslation()
  const list = usePayrollParams(open)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [draft, setDraft] = useState<PayrollParams | null>(null)
  const rows = list.data ?? []
  const current = draft ?? rows.find((r) => r.id === selectedId) ?? rows[0] ?? null

  const startNew = () => {
    const base = rows[0]
    const year = new Date().getFullYear() + 1
    if (base) setDraft({ ...base, id: '', validFrom: `${year}-01-01`, note: '' })
  }

  return (
    <Modal
      open={open}
      size="xl"
      onOpenChange={(v) => !v && onClose()}
      icon={<Scale />}
      title={t('hr.personel.bordro.params.title')}
      description={t('hr.personel.bordro.params.hint')}
    >
      {list.isLoading ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[14rem_minmax(0,1fr)]">
          <div className="space-y-2">
            <ul className="space-y-1.5">
              {rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setDraft(null)
                      setSelectedId(r.id)
                    }}
                    className={cn(
                      'hover:bg-muted w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      !draft && current?.id === r.id && 'border-primary/50 bg-primary/5',
                    )}
                  >
                    <span className="block font-medium">{fmtDate(r.validFrom)}</span>
                    <span className="text-muted-foreground block text-xs">
                      {t('hr.personel.bordro.params.minWage')}: {fmtMoney(r.minWageGross)}
                    </span>
                  </button>
                </li>
              ))}
              {draft && !draft.id ? (
                <li className="border-primary/50 bg-primary/5 rounded-lg border px-3 py-2 text-sm font-medium">
                  {t('hr.personel.bordro.params.newSet')}
                </li>
              ) : null}
            </ul>
            {canManage ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full"
                onClick={startNew}
                disabled={!rows.length}
              >
                <Plus />
                {t('hr.personel.bordro.params.newSet')}
              </Button>
            ) : null}
          </div>
          <div className="min-w-0 space-y-6">
            {current ? (
              <ParamsForm
                key={`${current.id || 'new'}-${current.validFrom}`}
                value={current}
                canManage={canManage}
                onSaved={(id) => {
                  setDraft(null)
                  if (id) setSelectedId(id)
                }}
                onCancelNew={() => setDraft(null)}
              />
            ) : null}
            <PreviewCalculator />
          </div>
        </div>
      )}
    </Modal>
  )
}

function ParamsForm({
  value,
  canManage,
  onSaved,
  onCancelNew,
}: {
  value: PayrollParams
  canManage: boolean
  onSaved: (id?: string) => void
  onCancelNew: () => void
}) {
  const { t } = useTranslation()
  const save = useSavePayrollParams()
  const del = useDeletePayrollParams()
  const [p, setP] = useState(value)
  const set = <K extends keyof PayrollParams>(k: K, v: PayrollParams[K]) => setP((cur) => ({ ...cur, [k]: v }))
  const setBracket = (i: number, k: 'upTo' | 'rate', v: string) =>
    setP((cur) => ({ ...cur, brackets: cur.brackets.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }))
  const ro = !canManage

  const submit = () =>
    save.mutate(p, {
      onSuccess: () => {
        toast.success(t('hr.personel.bordro.params.saved'))
        onSaved(p.id || undefined)
      },
      onError: (e) => toast.error(apiErrorMessage(e)),
    })
  const remove = async () => {
    const ok = await confirmModal({
      title: t('hr.personel.bordro.params.deleteTitle'),
      description: t('hr.personel.bordro.params.deleteBody', { date: fmtDate(p.validFrom) }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (ok)
      del.mutate(p.id, {
        onSuccess: () => toast.success(t('hr.personel.bordro.params.deleted')),
        onError: (e) => toast.error(apiErrorMessage(e)),
      })
  }

  const num = (id: string, k: keyof PayrollParams, label: string, suffix?: string) => (
    <Field id={id} label={label}>
      <NumberInput
        id={id}
        value={String(p[k])}
        onChange={(v) => set(k, v as never)}
        maxDecimals={4}
        suffix={suffix}
        disabled={ro}
      />
    </Field>
  )

  return (
    <div className="space-y-5">
      {p.id ? (
        <div className="flex flex-wrap gap-2">
          <Badge tone="info">
            {t('hr.personel.bordro.params.minWageNet')}: {fmtMoney(p.minWageNet)}
          </Badge>
          <Badge tone="outline">
            {t('hr.personel.bordro.params.minWageCost')}: {fmtMoney(p.minWageEmployerCost)}
          </Badge>
        </div>
      ) : null}
      <FormSection>
        <Field id="pp-from" label={t('hr.personel.bordro.params.validFrom')}>
          <DateField
            id="pp-from"
            value={p.validFrom}
            onChange={(v) => set('validFrom', v)}
            clearable={false}
            disabled={ro}
          />
        </Field>
        {num('pp-mw', 'minWageGross', t('hr.personel.bordro.params.minWage'), '₺')}
        {num('pp-ceil', 'sgkCeilingMultiplier', t('hr.personel.bordro.params.ceiling'), '×')}
        {num('pp-disc', 'employerDiscountRate', t('hr.personel.bordro.params.discount'), '%')}
        {num('pp-esgk', 'employeeSgkRate', t('hr.personel.bordro.params.employeeSgk'), '%')}
        {num('pp-eun', 'employeeUnempRate', t('hr.personel.bordro.params.employeeUnemp'), '%')}
        {num('pp-rsgk', 'employerSgkRate', t('hr.personel.bordro.params.employerSgk'), '%')}
        {num('pp-run', 'employerUnempRate', t('hr.personel.bordro.params.employerUnemp'), '%')}
        {num('pp-stamp', 'stampTaxRate', t('hr.personel.bordro.params.stamp'), '%')}
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-[var(--color-primary)]"
            checked={p.minWageExemption}
            disabled={ro}
            onChange={(e) => set('minWageExemption', e.target.checked)}
          />
          {t('hr.personel.bordro.params.exemption')}
        </label>
      </FormSection>

      <FormSection title={t('hr.personel.bordro.params.brackets')}>
        <div className="space-y-2 sm:col-span-2">
          {p.brackets.map((b, i) => {
            const last = i === p.brackets.length - 1
            return (
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_7rem_auto] items-center gap-2">
                {last ? (
                  <span className="text-muted-foreground text-sm">{t('hr.personel.bordro.params.lastBracket')}</span>
                ) : (
                  <NumberInput
                    aria-label={t('hr.personel.bordro.params.upTo')}
                    value={b.upTo}
                    onChange={(v) => setBracket(i, 'upTo', v)}
                    suffix="₺"
                    disabled={ro}
                  />
                )}
                <NumberInput
                  aria-label={t('hr.personel.bordro.params.rate')}
                  value={b.rate}
                  onChange={(v) => setBracket(i, 'rate', v)}
                  suffix="%"
                  disabled={ro}
                />
                {!ro && !last && p.brackets.length > 2 ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    aria-label={t('common.delete')}
                    onClick={() =>
                      set(
                        'brackets',
                        p.brackets.filter((_, j) => j !== i),
                      )
                    }
                  >
                    <Trash2 />
                  </Button>
                ) : (
                  <span className="size-8" />
                )}
              </div>
            )
          })}
          {!ro ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() =>
                set('brackets', [...p.brackets.slice(0, -1), { upTo: '', rate: '' }, ...p.brackets.slice(-1)])
              }
            >
              <Plus />
              {t('hr.personel.bordro.params.addBracket')}
            </Button>
          ) : null}
        </div>
        <Field id="pp-note" label={t('hr.personel.bordro.params.note')} full>
          <Input
            id="pp-note"
            value={p.note}
            maxLength={500}
            onChange={(e) => set('note', e.target.value)}
            disabled={ro}
          />
        </Field>
      </FormSection>

      {canManage ? (
        <div className="flex flex-wrap justify-end gap-2">
          {p.id ? (
            <Button
              type="button"
              variant="outline"
              className="text-destructive hover:text-destructive"
              disabled={del.isPending}
              onClick={() => void remove()}
            >
              <Trash2 />
              {t('common.delete')}
            </Button>
          ) : (
            <Button type="button" variant="cancel" onClick={onCancelNew}>
              {t('common.cancel')}
            </Button>
          )}
          <Button type="button" disabled={save.isPending} onClick={submit}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

const PREVIEW_ROWS: [string, string][] = [
  ['gross', 'hr.personel.ucret.brut'],
  ['employee_sgk', 'hr.personel.bordro.dokum.sgkIsciLabel'],
  ['employee_unemployment', 'hr.personel.bordro.dokum.issizlikIsciLabel'],
  ['income_tax', 'hr.personel.bordro.dokum.gelirVergisi'],
  ['stamp_tax', 'hr.personel.bordro.dokum.damgaVergisi'],
  ['net', 'hr.personel.ucret.net'],
  ['employer_cost', 'hr.personel.bordro.dokum.isverenMaliyet'],
]

function PreviewCalculator() {
  const { t } = useTranslation()
  const preview = usePayrollPreview()
  const [gross, setGross] = useState('')
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7))
  const [prior, setPrior] = useState('')
  const r = preview.data
  return (
    <FormSection title={t('hr.personel.bordro.params.calculator')}>
      <Field id="pc-gross" label={t('hr.personel.bordro.params.gross')}>
        <NumberInput id="pc-gross" value={gross} onChange={setGross} suffix="₺" />
      </Field>
      <Field id="pc-month" label={t('hr.personel.bordro.params.month')}>
        <Input id="pc-month" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
      </Field>
      <Field id="pc-prior" label={t('hr.personel.bordro.params.prior')}>
        <NumberInput id="pc-prior" value={prior} onChange={setPrior} suffix="₺" />
      </Field>
      <div className="flex items-end">
        <Button
          type="button"
          variant="outline"
          disabled={!Number(gross) || preview.isPending}
          onClick={() =>
            preview.mutate({ gross, period, priorTaxBase: prior }, { onError: (e) => toast.error(apiErrorMessage(e)) })
          }
        >
          {preview.isPending ? <Loader2 className="animate-spin" /> : <Calculator />}
          {t('hr.personel.bordro.params.calculate')}
        </Button>
      </div>
      {r ? (
        <dl className="divide-y rounded-lg border text-sm sm:col-span-2">
          {PREVIEW_ROWS.map(([k, label]) => (
            <div
              key={k}
              className={cn(
                'flex justify-between gap-2 px-3 py-1.5',
                (k === 'net' || k === 'employer_cost') && 'font-semibold',
              )}
            >
              <dt className="text-muted-foreground">{t(label)}</dt>
              <dd className="tabular-nums">{fmtMoney(r[k] ?? '0')}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </FormSection>
  )
}
